import * as React from 'react';
import { cn } from '@relay-towing/tokens';
import {
  chipRecipe,
  statusDotRecipe,
  type StatusDotRecipeProps,
} from '../recipes/badge.recipe';

export interface StatusDotProps
  extends React.HTMLAttributes<HTMLSpanElement>,
    StatusDotRecipeProps {
  /**
   * What a screen reader should announce — "Open faults". Omit when the dot
   * only colours a word already on screen; it is then hidden from assistive
   * technology.
   */
  label?: string;
}

/**
 * A filled circle in a status tone, on its own.
 *
 *   <StatusDot tone="destructive" size="sm" label="Open faults" />
 *
 * The badge draws its own dot; this is for the two places a dot stands
 * apart from a badge — leading a Chip, and in front of a time in a row.
 */
export const StatusDot = React.forwardRef<HTMLSpanElement, StatusDotProps>(
  function StatusDot({ tone, size, label, className, ...props }, ref) {
    return (
      <span
        ref={ref}
        role={label ? 'img' : undefined}
        aria-label={label}
        aria-hidden={label ? undefined : true}
        className={cn(statusDotRecipe({ tone, size }), className)}
        {...props}
      />
    );
  }
);

export interface ChipProps extends React.HTMLAttributes<HTMLSpanElement> {
  /** A 16px glyph before the words — a lucide icon, sized here. */
  icon?: React.ReactNode;
  /** A status dot before the words instead of a glyph, in this tone. */
  dot?: StatusDotRecipeProps['tone'];
}

/**
 * A fact about the thing on screen, said in four words beside its title.
 *
 *   <Chip dot="success">Passed</Chip>
 *   <Chip icon={<FileText />}>Daily walk-around</Chip>
 *
 * The report card, the checklist form and the truck card each draw a row of
 * these under the title. One component, so the three cannot drift.
 */
export const Chip = React.forwardRef<HTMLSpanElement, ChipProps>(function Chip(
  { icon, dot, className, children, ...props },
  ref
) {
  return (
    <span ref={ref} className={cn(chipRecipe(), className)} {...props}>
      {dot ? <StatusDot tone={dot} size="md" /> : icon}
      {children}
    </span>
  );
});
